const STORAGE_KEY = 'ch-cardiologia-browser-notifications';
const ICON_URL = '/favicon.png';

/** @type {Set<number>} */
const shownIds = new Set();

/** @type {Map<number, { id: number, titulo?: string, cuerpo?: string, [key: string]: unknown }>} */
const pendingById = new Map();

/** @type {Notification[]} */
let openNotifications = [];

export function isSupported() {
    return typeof window !== 'undefined'
        && 'Notification' in window
        && window.isSecureContext;
}

export function getPermission() {
    if (!isSupported()) return 'denied';
    return Notification.permission;
}

export function isOptedIn() {
    try {
        return localStorage.getItem(STORAGE_KEY) === 'on';
    } catch {
        return false;
    }
}

export function setOptedIn(value) {
    try {
        localStorage.setItem(STORAGE_KEY, value ? 'on' : 'off');
    } catch {
        // ignore quota / private mode
    }
}

/** Permiso + opt-in + contexto seguro (sin exigir pestaña oculta). */
export function canShow() {
    return isSupported()
        && isOptedIn()
        && getPermission() === 'granted';
}

/** Listo para mostrar ahora (incluye pestaña en segundo plano). */
export function shouldShowNow() {
    return canShow()
        && typeof document !== 'undefined'
        && document.visibilityState === 'hidden';
}

/**
 * Activa alertas nativas (pedir permiso solo si está en default).
 * Debe llamarse desde un gesto del usuario.
 * @returns {Promise<boolean>}
 */
export async function enable() {
    if (!isSupported()) return false;
    if (getPermission() === 'denied') return false;

    let permission = getPermission();
    if (permission === 'default') {
        permission = await Notification.requestPermission();
    }

    if (permission === 'granted') {
        setOptedIn(true);
        return true;
    }
    return false;
}

export function disable() {
    setOptedIn(false);
    clearPending();
    closeAll();
}

function trackOpen(notification) {
    openNotifications.push(notification);
    notification.addEventListener('close', () => {
        openNotifications = openNotifications.filter((n) => n !== notification);
    });
}

/**
 * Encola ítems para mostrar cuando la pestaña pase a segundo plano.
 * @param {Array<{ id: number, titulo?: string, cuerpo?: string }>} items
 */
export function enqueuePending(items) {
    if (!canShow() || !Array.isArray(items) || items.length === 0) return;
    items.forEach((n) => {
        if (!n || n.id == null || shownIds.has(n.id)) return;
        pendingById.set(n.id, n);
    });
}

export function clearPending() {
    pendingById.clear();
}

/**
 * Muestra alertas nativas para ítems nuevos (batch si hay más de uno).
 * @param {Array<{ id: number, titulo?: string, cuerpo?: string }>} items
 * @param {{ onClick?: (item: object) => void }} [options]
 */
export function showMany(items, { onClick } = {}) {
    if (!shouldShowNow() || !Array.isArray(items) || items.length === 0) return;

    const fresh = items
        .filter((n) => n && n.id != null && !shownIds.has(n.id))
        .sort((a, b) => b.id - a.id);

    if (!fresh.length) return;

    fresh.forEach((n) => {
        shownIds.add(n.id);
        pendingById.delete(n.id);
    });

    const target = fresh[0];
    const extra = fresh.length - 1;
    const title = target.titulo || 'Nueva notificación';
    const body = extra > 0
        ? `Y ${extra} más`
        : (target.cuerpo || '');
    const tag = extra > 0 ? `notif-batch-${target.id}` : `notif-${target.id}`;

    try {
        const notification = new Notification(title, {
            body,
            tag,
            icon: ICON_URL,
        });
        trackOpen(notification);
        notification.onclick = () => {
            notification.close();
            try {
                window.focus();
            } catch {
                // ignore
            }
            if (typeof onClick === 'function') onClick(target);
        };
    } catch {
        // Algunos entornos lanzan si el permiso cambió a mitad de camino
    }
}

/**
 * Muestra lo pendiente cuando la pestaña está oculta.
 * @param {{ onClick?: (item: object) => void }} [options]
 */
export function flushPending({ onClick } = {}) {
    if (!shouldShowNow() || pendingById.size === 0) return;
    const items = Array.from(pendingById.values());
    showMany(items, { onClick });
}

export function closeAll() {
    const list = openNotifications.slice();
    openNotifications = [];
    list.forEach((n) => {
        try {
            n.close();
        } catch {
            // ignore
        }
    });
}
